import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Images } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const srcOf = (p) => (p.src.startsWith("http") ? p.src : `${process.env.REACT_APP_BACKEND_URL}${p.src}`);
const detailText = (d) => (Array.isArray(d) ? d.map((e) => e.msg).join(" ") : d);

export const GalleryManager = ({ auth }) => {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileInput = useRef(null);

  const load = () => axios.get(`${API}/admin/gallery`, auth()).then((r) => setPhotos(r.data)).catch(() => {});

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/admin/gallery`, fd, auth());
      } catch (err) {
        toast.error(detailText(err.response?.data?.detail) || `Errore su ${f.name}`);
      }
    }
    setBusy(false);
    if (fileInput.current) fileInput.current.value = "";
    toast.success("Foto aggiornate");
    load();
  };

  const remove = async (p) => {
    if (!window.confirm("Rimuovere questa foto dal sito?")) return;
    setDeleting(p.id);
    try {
      await axios.delete(`${API}/admin/gallery/${p.id}`, auth());
      setPhotos((list) => list.filter((x) => x.id !== p.id));
    } catch {
      toast.error("Errore");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <section data-testid="admin-gallery-manager" className="mb-12 rounded-2xl border border-[rgba(11,31,58,0.2)] bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Images className="w-5 h-5 text-[#0B1F3A]" /> Foto del sito</h2>
          <p className="text-sm text-[#6F6466]">Le foto appaiono nella striscia "I nostri lavori" (Instagram) sul sito.</p>
        </div>
        <button
          data-testid="admin-gallery-upload-button"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#071528] transition-colors disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica foto
        </button>
        <input ref={fileInput} data-testid="admin-gallery-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={upload} className="hidden" />
      </div>

      {photos.length === 0 ? (
        <p data-testid="admin-gallery-empty" className="text-sm text-[#6F6466]">Nessuna foto. Carica le tue foto per sostituire quelle di esempio.</p>
      ) : (
        <div data-testid="admin-gallery-grid" className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {photos.map((p) => (
            <div key={p.id} data-testid={`admin-gallery-photo-${p.id}`} className="relative group aspect-[4/5] overflow-hidden rounded-xl bg-[#0B1F3A]/5">
              <img src={srcOf(p)} alt={p.caption || "Foto"} className="w-full h-full object-cover" />
              <button
                data-testid={`admin-gallery-delete-${p.id}`}
                onClick={() => remove(p)}
                disabled={deleting === p.id}
                className="absolute top-2 right-2 rounded-full bg-white/90 p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white disabled:opacity-40"
              >
                {deleting === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 text-[#0B1F3A]" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
